import create from "zustand";
import { produce } from "immer";

import { getS3FileKey, isEmpty } from "../utils/index.js";
import {
  DataService,
  clearCachedProfilePicture,
  cancelDataServiceRequestByKey
} from "../services/index.js";
import {
  ADMIN_MODULE,
  ENVIRONMENT_TYPES
} from "../../constants/account.constants.js";
import { getPermissibleActions } from "../../accessctrl/utils.js";
import { ASTERISK } from "../../accessctrl/constants.js";

// const ACCOUNT_CONTEXT_URL = "/signin/2/{0}/context";
const MEMBER_LIST_URL = "/user/2/{0}?page_number={1}&page_size=50&q={2}";
const MEMBER_LIST_WITH_GROUP_URL =
  "/user/2/{0}/assignee?page_number={1}&page_size={2}&q={3}";
const MEMBER_LIST_BY_ID_URL =
  "/user/2/{0}/members?page_number={1}&page_size={2}";
const PROFILE_URL = "/user/2/{0}/{1}";
const PORTAL_USER_PROFILE_URL = "/user/2/{0}/portal/{1}/{2}/{3}";
const PORTAL_PROFILE_URL =
  "/user/2/{0}/portal/{1}/{2}?page_number={3}&page_size=50&q={4}";

function getTemplateUrl(templateUrl, baseUrl) {
  if (baseUrl) return templateUrl.replace("/user/2/{0}", `${baseUrl}/user`);
  return templateUrl;
}

export const useUserStore = create((set, get) => {
  async function load() {
    const accountId = get().accountId;
    const appConfig = get().appConfig;
    let isAuthendicated = true;
    console.info("ACCID", accountId, appConfig);
    if (
      !window.accountDetails.UserDetails ||
      window.accountDetails.UserDetails.status === "error"
    ) {
      set({
        isAuthendicated: false
      });
      return null;
    }
    const currentUser = window.accountDetails.UserDetails;
    const storeObj = {
      isAuthendicated,
      currentUser,
      users: {},
      currentAppId: "",
      sandbox: [],
      permissions: {},
      currentEnvironment: {}
    };
    if (currentUser) {
      if (currentUser.ProfilePicture) {
        currentUser.ProfilePicture.large.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(currentUser.ProfilePicture.large.key)}`;
        currentUser.ProfilePicture.medium.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(currentUser.ProfilePicture.medium.key)}`;
        currentUser.ProfilePicture.original.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(currentUser.ProfilePicture.original.key)}`;
        currentUser.ProfilePicture.preview.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(currentUser.ProfilePicture.preview.key)}`;
        currentUser.ProfilePicture.small.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(currentUser.ProfilePicture.small.key)}`;
      }

      storeObj.users = {
        [currentUser._id]: { ...currentUser, lastUpdate: new Date().getTime() }
      };
    }
    const permissions = window.accountDetails.UserDetails.Permissions || {};

    const isAdmin = new Set(
      getPermissibleActions(permissions, ADMIN_MODULE, ASTERISK)
    ).has(ASTERISK);

    storeObj.permissions = permissions;
    storeObj.isAdmin = isAdmin;
    storeObj.lastUpdate = new Date().getTime();
    clearCachedProfilePicture();
    set(storeObj);
  }

  async function getUserProfile(userId, isPortal = false) {
    const accountId = get().accountId;
    const appConfig = get().appConfig;
    let [fetchUserProfile] = DataService.get(
      PROFILE_URL.replace("{0}", accountId).replace("{1}", userId)
    );
    if (isPortal) {
      let portalId = window.accountDetails.PortalData._id;
      let modelId = window.accountDetails.PortalData.IdentityType;
      [fetchUserProfile] = DataService.get(
        PORTAL_USER_PROFILE_URL.replace("{0}", accountId)
          .replace("{1}", portalId)
          .replace("{2}", modelId)
          .replace("{3}", userId)
      );
    }
    const user = await fetchUserProfile();
    if (user.ProfilePicture) {
      user.ProfilePicture.large.url = `${
        appConfig.ProfileBasePath
      }/${getS3FileKey(user.ProfilePicture.large.key)}`;
      user.ProfilePicture.medium.url = `${
        appConfig.ProfileBasePath
      }/${getS3FileKey(user.ProfilePicture.medium.key)}`;
      user.ProfilePicture.original.url = `${
        appConfig.ProfileBasePath
      }/${getS3FileKey(user.ProfilePicture.original.key)}`;
      user.ProfilePicture.preview.url = `${
        appConfig.ProfileBasePath
      }/${getS3FileKey(user.ProfilePicture.preview.key)}`;
      user.ProfilePicture.small.url = `${
        appConfig.ProfileBasePath
      }/${getS3FileKey(user.ProfilePicture.small.key)}`;
    }
    return user;
  }

  async function getUser(userId, cache = true) {
    let user = get().users[userId];
    let isPortal = window.accountDetails?.IsPortal;
    if (!user || !cache) {
      let profile = await getUserProfile(userId, isPortal);
      user = {
        _id: profile._id,
        Email: profile.Email,
        Name: `${profile.FirstName || ""} ${profile.LastName || ""}`.trim(),
        Kind: "User",
        ProfilePicture: profile.ProfilePicture,
        ...profile
      };

      if (get().currentUser._id !== user._id) {
        set(
          produce((state) => {
            state.users[user._id] = {
              ...user,
              lastUpdate: new Date().getTime()
            };
          })
        );
      }
    }
    return user;
  }

  async function updateProfilePicture(userId, ProfilePicture) {
    let user = get().users[userId];
    const appConfig = get().appConfig;
    if (ProfilePicture) {
      ProfilePicture.large.url = `${appConfig.ProfileBasePath}/${getS3FileKey(
        ProfilePicture.large.key
      )}`;
      ProfilePicture.medium.url = `${appConfig.ProfileBasePath}/${getS3FileKey(
        ProfilePicture.medium.key
      )}`;
      ProfilePicture.original.url = `${
        appConfig.ProfileBasePath
      }/${getS3FileKey(ProfilePicture.original.key)}`;
      ProfilePicture.preview.url = `${appConfig.ProfileBasePath}/${getS3FileKey(
        ProfilePicture.preview.key
      )}`;
      ProfilePicture.small.url = `${appConfig.ProfileBasePath}/${getS3FileKey(
        ProfilePicture.small.key
      )}`;
    }

    set(
      produce((state) => {
        state.users[user._id] = {
          ...user,
          ProfilePicture: ProfilePicture,
          lastUpdate: new Date().getTime()
        };
      })
    );
  }

  // eslint-disable-next-line max-params
  async function getUsers(
    query = "",
    page = 1,
    includeGroups = false,
    acceptInvitedUsers = false,
    skipServiceAccount = false,
    pageSize = 50,
    apiBaseUrl = "",
    id = "getUsers"
  ) {
    const accountId = get().accountId;
    const appConfig = get().appConfig;
    /** cancelling pending promises */
    cancelDataServiceRequestByKey(id);
    let apiURL = "";
    let isPortal = window.accountDetails?.IsPortal;

    if (includeGroups) {
      apiURL = getTemplateUrl(MEMBER_LIST_WITH_GROUP_URL, apiBaseUrl)
        .replace("{0}", accountId)
        .replace("{1}", page)
        .replace("{2}", pageSize)
        .replace("{3}", query);
    } else if (isPortal) {
      let portalId = window.accountDetails.PortalData._id;
      let modelId = window.accountDetails.PortalData.IdentityType;
      apiURL = PORTAL_PROFILE_URL.replace("{0}", accountId)
        .replace("{1}", portalId)
        .replace("{2}", modelId)
        .replace("{3}", page)
        .replace("{4}", query);
    } else {
      apiURL = getTemplateUrl(MEMBER_LIST_URL, apiBaseUrl)
        .replace("{0}", accountId)
        .replace("{1}", page)
        .replace("{2}", query);
    }
    const [fetchUserList] = DataService.get(apiURL, {
      query: {
        invited_user: acceptInvitedUsers,
        skip_service_account: skipServiceAccount
      },
      cancelPendingRequestkey: id
    });
    const users = await fetchUserList().catch((err) => []);
    const formattedUsers = users.map((user) => {
      if (user.ProfilePicture) {
        user.ProfilePicture.large.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(user.ProfilePicture.large.key)}`;
        user.ProfilePicture.medium.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(user.ProfilePicture.medium.key)}`;
        user.ProfilePicture.original.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(user.ProfilePicture.original.key)}`;
        user.ProfilePicture.preview.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(user.ProfilePicture.preview.key)}`;
        user.ProfilePicture.small.url = `${
          appConfig.ProfileBasePath
        }/${getS3FileKey(user.ProfilePicture.small.key)}`;
      }
      return user;
    });

    // Note: Update the store with users only when fetching from the production account, not the sandbox account.
    if (!apiBaseUrl) {
      set(
        produce((state) => {
          formattedUsers.forEach((user) => {
            if (get().currentUser._id !== user._id) {
              state.users[user._id] = {
                ...(state.users[user._id] || {}),
                ...user,
                lastUpdate: new Date().getTime()
              };
            }
          });
        })
      );
    }

    return formattedUsers;
  }

  function getCachedUsers(ids, fields) {
    return ids.reduce((acc, id) => {
      if (get().users[id]) {
        const user = get().users[id];
        let userObj = {
          Name: user.Name,
          Email: user.Email,
          _id: user._id
        };
        fields.forEach((field) => {
          if (user[field]) {
            userObj[field] = user[field];
          }
        });
        acc.push(userObj);
      }
      return acc;
    }, []);
  }

  async function getUserGroupInfo({
    ids = [],
    fields = ["Email", "Name", "_id", "Kind"],
    pageNumber = 1,
    query = "",
    pageSize = 50,
    canAllowAll = false
  }) {
    const accountId = get().accountId;
    const appConfig = get().appConfig;
    let storeUsers = getCachedUsers(ids, fields);
    if (storeUsers.length < ids.length) {
      let apiURL = MEMBER_LIST_WITH_GROUP_URL.replace("{0}", accountId)
        .replace("{1}", pageNumber)
        .replace("{2}", pageSize)
        .replace("{3}", query);
      const [fetchUserList] = DataService.post(apiURL, {
        data: {
          Ids: ids,
          Fields: fields
        },
        query: {
          allow_all: canAllowAll
        }
      });
      const users = await fetchUserList();
      const formattedUsers = users.map((user) => {
        if (user.ProfilePicture && user.Type === "User") {
          user.ProfilePicture.large.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.large.key)}`;
          user.ProfilePicture.medium.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.medium.key)}`;
          user.ProfilePicture.original.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.original.key)}`;
          user.ProfilePicture.preview.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.preview.key)}`;
          user.ProfilePicture.small.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.small.key)}`;
        }
        return user;
      });
      set(
        produce((state) => {
          formattedUsers.forEach((user) => {
            if (get().currentUser._id !== user._id) {
              state.users[user._id] = {
                ...(state.users[user._id] || {}),
                ...user,
                lastUpdate: new Date().getTime()
              };
            }
          });
        })
      );
      return formattedUsers;
    }
    return storeUsers;
  }

  async function getUsersById(
    ids = [],
    page = 1,
    pageSize = 50,
    fields = ["Designation", "Kind", "Email", "Groups"]
  ) {
    const accountId = get().accountId;
    const appConfig = get().appConfig;
    let storeUsers = getCachedUsers(ids, fields);

    if (storeUsers.length < ids.length) {
      const [fetchUserList] = DataService.post(
        MEMBER_LIST_BY_ID_URL.replace("{0}", accountId)
          .replace("{1}", page)
          .replace("{2}", pageSize),
        {
          data: { Ids: ids, Fields: fields },
          toast: false
        }
      );
      const users = await fetchUserList().catch((err) => []);
      const formattedUsers = users.map((user) => {
        if (user.ProfilePicture) {
          user.ProfilePicture.large.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.large.key)}`;
          user.ProfilePicture.medium.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.medium.key)}`;
          user.ProfilePicture.original.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.original.key)}`;
          user.ProfilePicture.preview.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.preview.key)}`;
          user.ProfilePicture.small.url = `${
            appConfig.ProfileBasePath
          }/${getS3FileKey(user.ProfilePicture.small.key)}`;
        }
        return user;
      });
      set(
        produce((state) => {
          formattedUsers.forEach((user) => {
            state.users[user._id] = {
              ...user,
              lastUpdate: new Date().getTime()
            };
          });
        })
      );
      return formattedUsers;
    }
    const fetchedUsers = [...storeUsers].sort((a, b) =>
      a.Name.localeCompare(b.Name)
    );
    return fetchedUsers;
  }

  async function setAccount(accountId, appConfig) {
    set({ accountId, appConfig });
  }

  function updateAppId(appId) {
    set(
      produce((state) => {
        state.currentAppId = appId;
      })
    );
  }

  function setSandbox(sandbox) {
    set(
      produce((state) => {
        state.sandbox = sandbox;
        state.currentEnvironment = sandbox.find(
          (env) => env._id === get().accountId
        ) || {
          Type: ENVIRONMENT_TYPES.PRODUCTION
        };
      })
    );
  }

  function fetchAndUpdateSandbox(forceUpdate = false) {
    let sandbox = get().sandbox;
    if (forceUpdate || isEmpty(sandbox)) {
      const [fetchSandbox] = DataService.get(
        `/accounts/2/${get().accountId}/sandbox`
      );
      fetchSandbox().then(get().setSandbox);
    }
  }

  function userTheme(value) {
    set({
      theme: value
    });
  }

  function update(stateFn) {
    set(produce(stateFn));
  }

  function updateSignature(signature) {
    set(
      produce((state) => {
        state.currentUser.Signature = signature;
      })
    );
  }

  function isSuperAdmin() {
    return get().isAdmin;
  }

  return {
    isAuthendicated: null,
    setAccount,
    load,
    getUser,
    getUsers,
    getUsersById,
    getUserProfile,
    update,
    updateProfilePicture,
    updateAppId,
    userTheme,
    updateSignature,
    setSandbox,
    fetchAndUpdateSandbox,
    isSuperAdmin,
    getUserGroupInfo
  };
});
